import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import OmTextField from '../components/ui/OmTextField';
import OmButton from '../components/ui/OmButton';
import OMLogo from '../assets/OM-logo.png';
import apiClient from '../utils/apiClient';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const toast = useToast();

  const handleChange = (field, e) => {
    setEmail(e.target.value);
    // Clear success message when user starts typing again
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!email?.trim()) {
      toast.showError('Email is required');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.showError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    
    // Store logs in an array to prevent them from disappearing
    const logs = [];
    
    try {
      logs.push('=== FORGOT PASSWORD DEBUG ===');
      logs.push('1. Email: ' + email);
      logs.push('2. API Base URL: ' + (import.meta.env.VITE_API_URL || 'http://localhost:3000/api'));
      logs.push('3. Endpoint: /auth/forgot-password');
      logs.push('4. Making request...');
      
      console.table(logs);
      
      const response = await apiClient.post('/auth/forgot-password', { email });
      
      logs.push('5. Response received!');
      logs.push('6. Response status: ' + response.status);
      logs.push('7. Response data: ' + JSON.stringify(response.data));
      
      console.table(logs);
      
      const message = response.data.data?.message || 'If an account exists with that email, a password reset link has been sent.';
      setSuccessMessage(message);
      toast.showSuccess('Password reset link sent to your email');
      setEmail('');
      
      logs.push('8. Success! Message displayed.');
      console.log('=== ALL LOGS ===');
      console.table(logs);
      
    } catch (error) {
      logs.push('=== ERROR CAUGHT ===');
      logs.push('Error type: ' + error.constructor.name);
      logs.push('Error message: ' + error.message);
      
      if (error.response) {
        logs.push('Response status: ' + error.response.status);
        logs.push('Response data: ' + JSON.stringify(error.response.data));
      }
      
      console.error('=== ERROR DETAILS ===');
      console.table(logs);
      console.error('Full error object:', error);
      
      // Even on error, show success message for security
      const message = 'If an account exists with that email, a password reset link has been sent.';
      setSuccessMessage(message);
      toast.showSuccess('Request processed');
      
    } finally {
      setIsLoading(false);
      logs.push('=== REQUEST COMPLETE ===');
      console.log('Final log count:', logs.length);
      console.table(logs);
      
      // Keep logs visible
      console.log('Logs preserved. Check console.');
    }
  };

  return (
    <div className="min-h-screen bg-silver-mist flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Header Section */}
        <div className="flex flex-col items-center space-y-6">
          <div className="flex-shrink-0">
            <img 
              src={OMLogo} 
              alt="Old Mutual Logo" 
              className="h-16 w-auto"
            />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-deep-jungle tracking-tight">
              Forgot Password
            </h1>
            <p className="text-steel text-sm font-medium">
              Enter your email address and we'll send you a password reset link
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div 
          className="bg-crystal-white shadow-lg border border-silver-mist mx-4 sm:mx-0 overflow-hidden"
          style={{ borderRadius: '24px' }}
        >
          <div className="px-6 py-8 space-y-6">
            {/* Success message display */}
            {successMessage && (
              <div className="p-4 bg-fresh-leaf/10 border-l-4 border-fresh-leaf rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-fresh-leaf" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-sm font-medium text-graphite leading-5">
                    {successMessage}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <OmTextField
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => handleChange('email', e)}
                  required
                  leadingIcon="email"
                />
              </div>

              <div className="pt-2">
                <OmButton
                  type="submit"
                  disabled={isLoading}
                  className="w-full justify-center text-base font-medium py-3 rounded-2xl"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </OmButton>
              </div>
            </form>

            {/* Link back to login */}
            <div className="text-center pt-4 border-t border-silver-mist">
              <Link 
                to="/login" 
                className="inline-flex items-center space-x-2 text-sm font-semibold text-fresh-leaf hover:text-forest-fern transition-all duration-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span>Back to Login</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
